import { useState } from 'react';

/**
 * Modal flow for vehicle disposition: Sold, Archive, or Delete.
 *
 * Props:
 * - vehicle: current vehicle object
 * - serviceHistory: array of service entries for this vehicle
 * - onSold(saleData): called when user confirms sale
 * - onArchive(): called when user confirms archive
 * - onDeletePermanently(): called when user confirms permanent deletion
 * - onClose(): dismiss modal
 */
export default function VehicleActionModal({
  vehicle,
  serviceHistory,
  onSold,
  onArchive,
  onDeletePermanently,
  onClose,
}) {
  // 'choose' | 'sold' | 'delete'
  const [step, setStep] = useState('choose');

  // Sold form state
  const [salePrice, setSalePrice] = useState('');
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [buyerType, setBuyerType] = useState('');
  const [saleNotes, setSaleNotes] = useState('');

  // Delete confirmation
  const [deleteText, setDeleteText] = useState('');

  // Calculate cost of ownership
  const maintenanceTotal = serviceHistory.reduce((sum, e) => sum + (Number(e.cost) || 0), 0);
  const purchasePrice = Number(vehicle.purchasePrice) || 0;
  const totalSpent = purchasePrice + maintenanceTotal;
  const salePriceNum = Number(salePrice) || 0;
  const netAmount = salePriceNum - totalSpent;

  // ── Choose step ──
  if (step === 'choose') {
    return (
      <Overlay onClose={onClose}>
        <h3 style={titleStyle}>What happened to this vehicle?</h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 24, textAlign: 'center' }}>
          {vehicle.year} {vehicle.make} {vehicle.model}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <ChoiceButton
            emoji="🚗"
            label="Sold"
            desc="Record the sale and track profit/loss"
            onClick={() => setStep('sold')}
          />
          <ChoiceButton
            emoji="📦"
            label="Archive"
            desc="Move to archived — can be restored later"
            onClick={onArchive}
          />
          <ChoiceButton
            emoji="🗑️"
            label="Delete Permanently"
            desc="Remove all data — cannot be undone"
            onClick={() => setStep('delete')}
            danger
          />
        </div>

        <button
          onClick={onClose}
          style={{ ...btnSecondary, marginTop: 16 }}
        >
          Cancel
        </button>
      </Overlay>
    );
  }

  // ── Sold step ──
  if (step === 'sold') {
    const BUYER_TYPES = ['Private Party', 'Dealer', 'Trade-in'];
    return (
      <Overlay onClose={onClose}>
        <h3 style={titleStyle}>Record Sale</h3>

        <div className="form-group">
          <label className="form-label">Sale Price</label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-muted)', fontSize: '0.9375rem', fontWeight: 600,
            }}>$</span>
            <input
              className="form-input"
              type="number"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              placeholder="0"
              style={{ paddingLeft: 28 }}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Sale Date</label>
          <input
            className="form-input"
            type="date"
            value={saleDate}
            onChange={(e) => setSaleDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Buyer Type</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {BUYER_TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setBuyerType(t)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 100,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  border: `1.5px solid ${buyerType === t ? 'var(--accent)' : 'var(--border)'}`,
                  background: buyerType === t ? 'var(--accent-subtle)' : 'transparent',
                  color: buyerType === t ? 'var(--accent)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Notes (optional)</label>
          <textarea
            className="form-input"
            rows={3}
            value={saleNotes}
            onChange={(e) => setSaleNotes(e.target.value)}
            placeholder="Any notes about the sale..."
            style={{ resize: 'vertical', minHeight: 60, fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>

        {/* Cost of Ownership Summary */}
        <div style={{
          background: 'var(--bg-elevated)',
          borderRadius: 'var(--radius-sm)',
          padding: 16,
          marginTop: 8,
          marginBottom: 16,
        }}>
          <h4 style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
            Cost of Ownership
          </h4>
          {purchasePrice > 0 && (
            <Row label="Purchase price" value={`$${purchasePrice.toLocaleString()}`} />
          )}
          <Row label="Maintenance total" value={`$${maintenanceTotal.toLocaleString()}`} />
          <Row label="Total spent" value={`$${totalSpent.toLocaleString()}`} bold />
          {salePriceNum > 0 && (
            <>
              <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
              <Row label="Sold for" value={`$${salePriceNum.toLocaleString()}`} />
              <Row
                label="Net"
                value={`${netAmount >= 0 ? '+' : '-'}$${Math.abs(netAmount).toLocaleString()}`}
                bold
                color={netAmount >= 0 ? '#22c55e' : 'var(--danger)'}
              />
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setStep('choose')} style={btnSecondary}>Back</button>
          <button
            onClick={() => onSold({
              salePrice: salePriceNum,
              saleDate,
              buyerType,
              saleNotes,
              maintenanceTotal,
              purchasePrice,
              totalSpent,
              netAmount: salePriceNum > 0 ? netAmount : null,
            })}
            disabled={!salePrice || !buyerType}
            style={{
              ...btnPrimary,
              opacity: (!salePrice || !buyerType) ? 0.5 : 1,
              cursor: (!salePrice || !buyerType) ? 'not-allowed' : 'pointer',
            }}
          >
            Confirm Sale
          </button>
        </div>
      </Overlay>
    );
  }

  // ── Delete step ──
  if (step === 'delete') {
    return (
      <Overlay onClose={onClose}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: 'rgba(239, 68, 68, 0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        <h3 style={{ ...titleStyle, color: 'var(--danger)' }}>Delete Permanently</h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
          This will permanently remove <strong style={{ color: 'var(--text-primary)' }}>
            {vehicle.year} {vehicle.make} {vehicle.model}
          </strong> and all associated data. This action cannot be undone.
        </p>

        <div className="form-group">
          <label className="form-label">Type DELETE to confirm</label>
          <input
            className="form-input"
            type="text"
            value={deleteText}
            onChange={(e) => setDeleteText(e.target.value)}
            placeholder="DELETE"
            style={{ fontFamily: 'monospace', letterSpacing: '0.1em', textAlign: 'center' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setStep('choose')} style={btnSecondary}>Back</button>
          <button
            onClick={onDeletePermanently}
            disabled={deleteText !== 'DELETE'}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              background: deleteText === 'DELETE' ? '#ef4444' : 'var(--bg-elevated)',
              color: deleteText === 'DELETE' ? 'white' : 'var(--text-muted)',
              cursor: deleteText === 'DELETE' ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
            }}
          >
            Delete Forever
          </button>
        </div>
      </Overlay>
    );
  }

  return null;
}

/* ─── Shared sub-components ─── */

function Overlay({ children, onClose }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-md)',
          padding: 24, width: '100%', maxWidth: 380,
          border: '1px solid var(--border)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ChoiceButton({ emoji, label, desc, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px',
        borderRadius: 'var(--radius-sm)',
        border: `1.5px solid ${danger ? 'var(--danger)' : 'var(--border)'}`,
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{emoji}</span>
      <div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: danger ? 'var(--danger)' : 'var(--text-primary)' }}>{label}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
      </div>
    </button>
  );
}

function Row({ label, value, bold, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{
        fontSize: '0.8125rem',
        fontWeight: bold ? 700 : 500,
        color: color || 'var(--text-primary)',
      }}>{value}</span>
    </div>
  );
}

/* ─── Shared styles ─── */

const titleStyle = {
  fontSize: '1.125rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
  textAlign: 'center',
  marginBottom: 8,
};

const btnSecondary = {
  flex: 1,
  padding: '12px 16px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
};

const btnPrimary = {
  flex: 1,
  padding: '12px 16px',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--accent)',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
};
